import { CanceledError, isAxiosError } from 'axios';
import { useMutationState, useQueryClient } from '@tanstack/react-query';

import { API_REQUEST_MUTATION_KEY } from '../constants';

const requestIdAbortControllerMap = new Map<string, AbortController>();

export const useApiRequestState = (requestId: string) => {
  const queryClient = useQueryClient();

  const mutationState = useMutationState({
    filters: { mutationKey: [API_REQUEST_MUTATION_KEY, requestId] },
    select: (mutation) => ({
      status: mutation.state.status,
      error: mutation.state.error,
    }),
  });

  const resetMutationState = (requestId: string) => {
    const mutation = queryClient.getMutationCache().find({ mutationKey: [API_REQUEST_MUTATION_KEY, requestId] });

    if (mutation) {
      queryClient.getMutationCache().remove(mutation);
    }
  };

  const registerAbortController = (controller: AbortController) => {
    requestIdAbortControllerMap.set(requestId, controller);
  };

  const unregisterAbortController = () => {
    requestIdAbortControllerMap.delete(requestId);
  };

  const cancelRequest = () => {
    const controller = requestIdAbortControllerMap.get(requestId);
    if (controller) {
      controller.abort();
      unregisterAbortController();
    }
  };

  const currentState = mutationState?.[mutationState.length - 1];

  return {
    isLoading: currentState?.status === 'pending',
    isError: currentState?.status === 'error' && !isAbortError(currentState?.error), // abort does not trigger isError
    isSuccess: currentState?.status === 'success',
    error: currentState?.error,
    resetMutationState,
    registerAbortController,
    unregisterAbortController,
    cancelRequest,
  };
};

export const isAbortError = (error?: Error | null): boolean => {
  const isAxiosCanceled = error instanceof CanceledError || (isAxiosError(error) && error?.code === 'ERR_CANCELED');
  const isCallableFnCancelled =
    error?.name === 'FirebaseError' &&
    [
      // thrown when aborted before streaming starts
      'functions/cancelled',
      // thrown when aborted during streaming (Firebase limitation)
      // - https://github.com/firebase/firebase-functions/issues/1684
      // - https://github.com/firebase/firebase-android-sdk/issues/6138
      'functions/internal',
    ].includes((error as any)?.code);

  return !!error && (isAxiosCanceled || isCallableFnCancelled);
};
